import React from 'react';
import moment from 'moment-timezone';
import I18n from 'modules/i18n';
import Tooltip from '@/components/common/TCIReactTooltip';
import styles from '@/components/staffer/AssignmentsBasedGradesTable/Table/styles.module.scss';
import { IAssignment } from '@/components/interfaces';
import useGradesTableStore from '@/components/staffer/AssignmentsBasedGradesTable/store/useGradesTableStore';
import cn from '@/utils/cn';
import {
  AssignmentDrawerButton,
} from '@/components/staffer/AssignmentsBasedGradesTable/Table/Rows/AssignmentDrawerButton';
import { queryClient } from '@/components/staffer/AssignmentsBasedGradesTable/QueryClientProvider';
import {
  getAssignableItems,
  progressQuery,
} from '@/components/staffer/AssignmentsBasedGradesTable/ReadingAssignmentsTable/utils';
import { sanitizeInlineHtml } from '@/utils/sanitizeInlineHtml';
import { filterAssignmentsByType } from '@/components/staffer/AssignmentsBasedGradesTable/Table/utils';
import AssignmentOptionsMenu from './AssignmentOptionsMenu';

const AssignmentsRow = () => {
  const classroom = useGradesTableStore(state => state.classroom);
  const allAssignments = useGradesTableStore(state => state.assignments);
  const assignmentTypeFilter = useGradesTableStore(state => state.filters.assignmentType);
  const allLessons = useGradesTableStore(state => state.allLessons);
  const assignments = filterAssignmentsByType(allAssignments, assignmentTypeFilter);

  const assignmentTypeCell = (assignment: IAssignment) => (
    <td
      key={`assignment-type-${assignment.id}`}
      className={styles.assignmentType}
    >
      {!assignment.investigation && assignment.assignment_details.type}
    </td>
  );

  const assignmentDetailsCell = (assignment: IAssignment, lastCell) => {
    const timezone = moment.tz(moment.tz.guess()).zoneAbbr();
    const assignmentTypes = Object.keys(assignment.assignment_models_hash);
    const isReadingAssignment = !assignment.investigation
      && ['Section', 'ReadingGame', 'Notebook'].some(type => assignmentTypes.includes(type));

    const tooltipContent = (
      <div>
        <div>
          <span className="tw-font-bold">{assignment.assignment_details.type}: </span>
          <span dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(assignment.assignment_details.title) }} />
        </div>
        <div>
          <span>{I18n.t('due_date')}: </span>
          <span className="tw-italic">{moment(assignment.due_datetime).format('M/DD/YY')} {timezone}</span>
        </div>
      </div>
    );

    return (
      <th
        key={assignment.id}
        className={cn(styles.assignmentDetails, { [styles.lasCell]: lastCell })}
        onMouseEnter={async () => {
          if (!isReadingAssignment) return;

          const assignedItems = getAssignableItems(assignment);
          const notebookIds = assignedItems
            .filter(assignedItem => assignedItem.assignment_type === 'Notebook')
            .map(assignedItem => assignedItem.id);

          const readingGameIds = assignedItems
            .filter(assignedItem => assignedItem.assignment_type === 'ReadingGame')
            .map(assignedItem => assignedItem.id);

          if (notebookIds.length > 0) {
            queryClient.prefetchQuery({
              queryFn: () => progressQuery(classroom, notebookIds, 'Notebook').then(res => res.data.data),
              queryKey: [classroom.id, 'Notebook', notebookIds.join(',')],
              staleTime: 60 * 1000,
            });
          }

          if (readingGameIds.length > 0) {
            queryClient.prefetchQuery({
              queryFn: () => progressQuery(classroom, readingGameIds, 'ReadingGame').then(res => res.data.data),
              queryKey: [classroom.id, 'ReadingGame', readingGameIds.join(',')],
              staleTime: 60 * 1000,
            });
          }
        }}
      >
        <Tooltip
          delay={400}
          html={tooltipContent}
          position="top"
          size="small"
          theme="white"
        >
          <div className="tw-flex tw-flex-row tw-items-center tw-justify-between">
            <div>
              <div className={cn(styles.assignmentTitle, 'tw-py-0.5')}>
                {allLessons && assignment.assignment_details.position_label && (
                  <span className="tw-font-bold">{assignment.assignment_details.position_label} | </span>
                )}
                <span dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(assignment.assignment_details.title) }} />
              </div>
              <div className="tw-italic tw-font-light tw-text-very-dark-grey tw-py-0.5 tw-flex tw-justify-between tw-items-center">
                {I18n.t('due')} {moment(assignment.due_datetime).format('M/DD/YY')}
                {isReadingAssignment && <AssignmentDrawerButton assignment={assignment} />}
              </div>
              <div className="tw-py-0.5">{assignment.max_points} {I18n.t('points')}</div>
            </div>

            <AssignmentOptionsMenu assignment={assignment} />
          </div>
        </Tooltip>
      </th>
    );
  };

  const assignmentTypeSpacer = (keyIdentifier: string) => (
    <td
      key={`assignment-type-spacer-${keyIdentifier}`}
      className={cn(styles.assignmentType, styles.spacer, '!tw-p-0 !tw-border-none tw-h-4')}
    />
  );

  const assignmentDetailSpacer = (label?: string, keyIdentifier?: string) => (
    <th
      key={`assignment-detail-spacer-${label || keyIdentifier || 'default'}`}
      className={cn(
        styles.assignmentDetails, styles.spacer,
        '!tw-p-0 tw-h-14',
        { 'tw-text-center tw-align-middle': label },
      )}
    >
      {label && (<p className="tw-m-0 tw-italic tw-text-dark-grey">{I18n.t(label)}</p>)}
    </th>
  );

  const renderAssignmentTypes = () => {
    if (assignments.length < 1) {
      return (
        <>
          {Array.from({ length: 4 }).map((_, i) => (assignmentTypeSpacer(i.toString())))}
        </>
      );
    }

    if (assignments.length < 3) {
      return (
        <>
          {assignments.map(a => assignmentTypeCell(a))}
          {Array.from({ length: 4 - assignments.length }).map((_, i) => (assignmentTypeSpacer(i.toString())))}
        </>
      );
    }

    return assignments.map(a => assignmentTypeCell(a));
  };

  const renderAssignmentDetails = () => {
    if (assignments.length < 1) {
      const emptyStateLabel = allAssignments.length < 1 ? 'no_assignments' : 'no_matching_assignments';

      return (
        <>
          {assignmentDetailSpacer(emptyStateLabel)}
          {Array.from({ length: 3 }).map((_, i) => (assignmentDetailSpacer(null, i.toString())))}
        </>
      );
    }

    if (assignments.length < 3) {
      return (
        <>
          {assignments.map((a, i) => assignmentDetailsCell(a, i === assignments.length - 1))}
          {Array.from({ length: 4 - assignments.length }).map((_, i) => (assignmentDetailSpacer(null, i.toString())))}
        </>
      );
    }

    return assignments.map((a, i) => assignmentDetailsCell(a, i === assignments.length - 1));
  };

  return (
    <>
      <tr>
        {assignmentTypeSpacer('type-spacer')}
        {renderAssignmentTypes()}
      </tr>
      <tr>
        {assignmentDetailSpacer()}
        {renderAssignmentDetails()}
      </tr>
    </>
  );
};

export default AssignmentsRow;
